import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Heart, TrendingUp, Target, ShieldAlert, Award } from "lucide-react";

const AREA_LABELS = {
  frente: "Líneas en la frente / entrecejo",
  ojos: "Patas de gallo",
  ojeras: "Ojeras",
  pomulos: "Pómulos / mejillas",
  nasogeniano: "Líneas nasogenianas",
  labios: "Labios",
  menton: "Mentón / papada",
  piel: "Calidad de piel",
  asesoria: "Pidieron asesoría",
};

const FRENO_LABELS = {
  miedo: "Miedo a que se note",
  precio: "El precio",
  empezar: "No saber por dónde empezar",
  listo: "Ya estaban listas/os",
};

const OBJETIVO_LABELS = {
  descanso: "Verse descansada/o",
  suave: "Suavizar líneas",
  realzar: "Realzar facciones",
  asesoria: "Sólo asesoría",
};

const SCORE_LABELS = {
  low: { label: "Autoestima alta", color: "#7A9180", desc: "Se sienten bien con su imagen" },
  mid: { label: "Inseguridad ocasional", color: "#A69076", desc: "Algo les afecta el día a día" },
  high: { label: "Necesitan atención", color: "#965D5D", desc: "Su autoimagen las/los frena" },
};

const Bar = ({ value, max, color = "#A69076" }) => (
  <div className="h-2 rounded-full bg-[#F4F0EB] overflow-hidden">
    <div className="h-full rounded-full" style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, background: color }} />
  </div>
);

export default function QuizPanel() {
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([api.get("/quiz/stats"), api.get("/quiz/submissions")]);
        setStats(a.data);
        setSubs(b.data);
      } catch (_) { /* noop */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="text-sm text-[#5C5E5C]" data-testid="quiz-panel-loading">Cargando…</div>;
  if (!stats || stats.total === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#E6E2DC] p-12 text-center" data-testid="quiz-panel-empty">
        <Heart className="w-10 h-10 text-[#A69076] mx-auto mb-4" />
        <h3 className="font-display text-2xl text-[#1A1C1A]">Aún nadie ha completado el quiz</h3>
        <p className="text-sm text-[#5C5E5C] mt-2 max-w-md mx-auto">
          Cuando pacientes potenciales terminen el Quiz de Autoestima en tu sitio, verás aquí qué áreas demandan más y podrás enfocar tus campañas.
        </p>
      </div>
    );
  }

  const maxArea = Math.max(...stats.top_areas.map(([, n]) => n), 1);
  const maxTreatment = Math.max(...stats.top_treatments.map(([, n]) => n), 1);

  return (
    <div className="space-y-8" data-testid="quiz-panel">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Heart} label="Quizzes completados" value={stats.total} accent />
        <Kpi icon={TrendingUp} label="Necesitan atención" value={stats.score_distribution.high} sub="autoestima baja" />
        <Kpi icon={Target} label="Inseguridad media" value={stats.score_distribution.mid} sub="potencial alto" />
        <Kpi icon={Award} label="Autoestima alta" value={stats.score_distribution.low} sub="ya se ven bien" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* TOP AREAS */}
        <div className="bg-white rounded-2xl border border-[#E6E2DC] p-7" data-testid="quiz-top-areas">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Áreas más demandadas</div>
          <h3 className="font-display text-xl text-[#1A1C1A] mb-5">¿Qué quieren mejorar?</h3>
          <div className="space-y-4">
            {stats.top_areas.length === 0 && <p className="text-sm text-[#5C5E5C]">Sin datos.</p>}
            {stats.top_areas.map(([area, count]) => (
              <div key={area}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-[#1A1C1A]">{AREA_LABELS[area] || area}</span>
                  <span className="text-[#5C5E5C]">{count}</span>
                </div>
                <Bar value={count} max={maxArea} />
              </div>
            ))}
          </div>
        </div>

        {/* TOP TREATMENTS */}
        <div className="bg-white rounded-2xl border border-[#E6E2DC] p-7" data-testid="quiz-top-treatments">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Tratamientos sugeridos</div>
          <h3 className="font-display text-xl text-[#1A1C1A] mb-5">Oportunidades de venta</h3>
          <div className="space-y-4">
            {stats.top_treatments.length === 0 && <p className="text-sm text-[#5C5E5C]">Sin datos.</p>}
            {stats.top_treatments.map(([t, count]) => (
              <div key={t}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-[#1A1C1A]">{t}</span>
                  <span className="text-[#5C5E5C]">{count}</span>
                </div>
                <Bar value={count} max={maxTreatment} color="#7A9180" />
              </div>
            ))}
          </div>
        </div>

        {/* FRENOS */}
        <div className="bg-white rounded-2xl border border-[#E6E2DC] p-7" data-testid="quiz-frenos">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Objeciones principales</div>
          <h3 className="font-display text-xl text-[#1A1C1A] mb-5">¿Qué las/los ha frenado?</h3>
          <div className="space-y-3">
            {stats.frenos.length === 0 && <p className="text-sm text-[#5C5E5C]">Sin datos.</p>}
            {stats.frenos.map(([f, c]) => (
              <div key={f} className="flex justify-between text-sm">
                <span className="text-[#1A1C1A]">{FRENO_LABELS[f] || f}</span>
                <span className="font-medium text-[#A69076]">{c}</span>
              </div>
            ))}
          </div>
        </div>

        {/* OBJETIVOS */}
        <div className="bg-white rounded-2xl border border-[#E6E2DC] p-7" data-testid="quiz-objetivos">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Resultado deseado</div>
          <h3 className="font-display text-xl text-[#1A1C1A] mb-5">¿Qué buscan?</h3>
          <div className="space-y-3">
            {stats.objetivos.length === 0 && <p className="text-sm text-[#5C5E5C]">Sin datos.</p>}
            {stats.objetivos.map(([o, c]) => (
              <div key={o} className="flex justify-between text-sm">
                <span className="text-[#1A1C1A]">{OBJETIVO_LABELS[o] || o}</span>
                <span className="font-medium text-[#A69076]">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RECENT SUBMISSIONS */}
      <div className="bg-white rounded-2xl border border-[#E6E2DC] overflow-hidden" data-testid="quiz-recent">
        <div className="px-7 py-5 border-b border-[#E6E2DC]">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Quizzes recientes</div>
          <h3 className="font-display text-xl text-[#1A1C1A]">Últimas 25 respuestas</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F4F0EB] text-left text-[10px] tracking-[0.25em] uppercase text-[#5C5E5C]">
            <tr>
              <th className="px-5 py-3">Fecha</th>
              <th className="px-5 py-3">Autoestima</th>
              <th className="px-5 py-3">Áreas de interés</th>
              <th className="px-5 py-3">Tratamientos sugeridos</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E6E2DC]">
            {subs.slice(0, 25).map((s) => {
              const bucket = s.score <= 2 ? "low" : s.score <= 5 ? "mid" : "high";
              const meta = SCORE_LABELS[bucket];
              return (
                <tr key={s.id}>
                  <td className="px-5 py-4 text-xs text-[#5C5E5C]">{new Date(s.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                      <span className="text-xs text-[#1A1C1A]">{meta.label}</span>
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs text-[#5C5E5C]">{(s.areas || []).map((a) => AREA_LABELS[a] || a).join(", ") || "—"}</td>
                  <td className="px-5 py-4 text-xs text-[#5C5E5C]">{(s.treatments || []).join(", ") || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, sub, accent }) => (
  <div className={`rounded-2xl border p-5 ${accent ? "bg-[#1A1C1A] text-[#FAFAFA] border-[#1A1C1A]" : "bg-white border-[#E6E2DC]"}`}>
    <Icon className={`w-4 h-4 mb-3 ${accent ? "text-[#A69076]" : "text-[#A69076]"}`} />
    <div className={`font-display text-3xl ${accent ? "text-[#FAFAFA]" : "text-[#1A1C1A]"}`}>{value}</div>
    <div className={`text-[10px] tracking-[0.25em] uppercase mt-1 ${accent ? "text-[#A69076]" : "text-[#5C5E5C]"}`}>{label}</div>
    {sub && <div className={`text-xs mt-1 ${accent ? "text-[#D9CDBC]" : "text-[#5C5E5C]"}`}>{sub}</div>}
  </div>
);
